import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ujmsPersons, ujmsPerson, ujmsCreatePerson, ujmsAddAlias, ujmsAddIdentifier } from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Search, UserPlus } from "lucide-react";
import { PersonReliefSummary } from "../enterprise/relief/PersonReliefSummary";
import { AccessChip } from "../enterprise/relief/AccessChip";

export default function RecordsPersons() {
  const [sf, setSf] = useState({ first_name: "", last_name: "", dob: "", alias: "" });
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ first_name: "", last_name: "", middle_name: "", suffix: "", dob: "", gender: "", preferred_name: "", aliases: "" });
  const [newAlias, setNewAlias] = useState("");
  const [newId, setNewId] = useState({ identifier_type: "SID", value: "" });

  const search = async () => {
    const params = {};
    Object.entries(sf).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await ujmsPersons(Object.keys(params).length ? params : { q: "" });
    setRows(r.data);
  };
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (id) => { const r = await ujmsPerson(id); setDetail(r.data); };

  const create = async () => {
    try {
      const payload = { ...np, aliases: np.aliases ? np.aliases.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      const r = await ujmsCreatePerson(payload);
      toast.success(r.data.duplicate_of_existing ? "Matched existing person — no duplicate created" : "PERSON_CREATED");
      setCreating(false); search();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="records-persons">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Records — Person Master</h1>
          <p className="text-sm text-slate-500">One identity per human being. Sensitive identifiers are masked; values never leave the vault.</p>
        </div>
        <button className={btnPrimary} onClick={() => setCreating(true)} data-testid="person-create-btn"><UserPlus className="w-4 h-4" /> New Person</button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="First Name"><input className={inputCls} value={sf.first_name} onChange={(e) => setSf({ ...sf, first_name: e.target.value })} data-testid="person-search-first" /></Field>
        <Field label="Last Name"><input className={inputCls} value={sf.last_name} onChange={(e) => setSf({ ...sf, last_name: e.target.value })} data-testid="person-search-last" /></Field>
        <Field label="DOB"><input type="date" className={inputCls} value={sf.dob} onChange={(e) => setSf({ ...sf, dob: e.target.value })} /></Field>
        <Field label="Alias"><input className={inputCls} value={sf.alias} onChange={(e) => setSf({ ...sf, alias: e.target.value })} /></Field>
        <button className={btnGhost} onClick={search} data-testid="person-search-btn"><Search className="w-4 h-4" /> Search</button>
      </div>

      <Panel testId="persons-table">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Name</th><th className={thCls}>DOB</th><th className={thCls}>Cases</th><th className={thCls}></th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className={tdCls}>{p.display_name}{p.preferred_name && <span className="text-xs text-slate-400 ml-2">“{p.preferred_name}”</span>}</td>
                <td className={`${tdCls} font-mono text-xs`}>{p.dob || "—"}</td>
                <td className={tdCls}>{p.case_count}</td>
                <td className={tdCls}><button className={btnGhost} onClick={() => open(p.id)} data-testid={`person-open-${p.id}`}>View Summary</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className={tdCls}>No persons found.</td></tr>}
          </tbody>
        </table>
      </Panel>

      {/* Person detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="person-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.display_name} {detail?.dob ? `— DOB ${detail.dob}` : ""}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Aliases</div>
                <div className="flex flex-wrap gap-1.5">
                  {(detail.aliases || []).map((a) => <span key={a.id} className="text-xs border border-slate-200 rounded-sm px-2 py-0.5">{a.name} <span className="text-slate-400">({a.alias_type})</span></span>)}
                  {!detail.aliases?.length && <span className="text-xs text-slate-400">None</span>}
                </div>
                <div className="flex gap-2 mt-2">
                  <input className={inputCls} placeholder="Add alias…" value={newAlias} onChange={(e) => setNewAlias(e.target.value)} data-testid="person-alias-input" />
                  <button className={btnGhost} disabled={!newAlias.trim()} data-testid="person-alias-add"
                    onClick={async () => { await ujmsAddAlias(detail.id, { name: newAlias }); setNewAlias(""); open(detail.id); toast.success("Alias recorded"); }}>Add</button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Identifiers (masked)</div>
                {(detail.identifiers || []).map((i) => (
                  <div key={i.id} className="font-mono text-xs py-0.5">{i.identifier_type}: ••••{i.last_four} <span className="text-slate-400">({i.source})</span></div>
                ))}
                {!detail.identifiers?.length && <span className="text-xs text-slate-400">None on file</span>}
                <div className="flex gap-2 mt-2">
                  <select className={`${selectCls} w-28`} value={newId.identifier_type} onChange={(e) => setNewId({ ...newId, identifier_type: e.target.value })} data-testid="person-id-type">
                    {["SID", "SSN", "FBI", "DL", "FOID", "OTHER"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input className={inputCls} placeholder="Value (stored encrypted)" value={newId.value} onChange={(e) => setNewId({ ...newId, value: e.target.value })} data-testid="person-id-value" />
                  <button className={btnGhost} disabled={!newId.value.trim()} data-testid="person-id-add"
                    onClick={async () => { await ujmsAddIdentifier(detail.id, newId); setNewId({ ...newId, value: "" }); open(detail.id); toast.success("Identifier vaulted"); }}>Add</button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Case involvement</div>
                {(detail.case_links || []).map((l) => (
                  <div key={l.id} className="flex items-center gap-2 py-0.5">
                    <Badge value={l.relationship_type} />
                    <Link to={`/cases/${l.case_id}`} className="text-blue-900 text-xs font-mono hover:underline" onClick={() => setDetail(null)} data-testid={`person-case-link-${l.case_id}`}>{l.case_number || "Open case record"}</Link>
                    <span className="text-xs text-slate-500 truncate">{l.caption}</span><AccessChip state={l.record_access} testId={`person-case-access-${l.case_id}`} />
                  </div>
                ))}
                {!detail.case_links?.length && <span className="text-xs text-slate-400">No case links</span>}
              </div>
              <div className="ent-panel p-1.5"><PersonReliefSummary personId={detail.id} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create person */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="person-create-dialog">
          <DialogHeader><DialogTitle>Create Person Record</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Legal First Name" required><input className={inputCls} value={np.first_name} onChange={(e) => setNp({ ...np, first_name: e.target.value })} data-testid="np-first" /></Field>
            <Field label="Legal Last Name" required><input className={inputCls} value={np.last_name} onChange={(e) => setNp({ ...np, last_name: e.target.value })} data-testid="np-last" /></Field>
            <Field label="Middle Name"><input className={inputCls} value={np.middle_name} onChange={(e) => setNp({ ...np, middle_name: e.target.value })} /></Field>
            <Field label="Suffix"><input className={inputCls} value={np.suffix} onChange={(e) => setNp({ ...np, suffix: e.target.value })} /></Field>
            <Field label="DOB"><input type="date" className={inputCls} value={np.dob} onChange={(e) => setNp({ ...np, dob: e.target.value })} data-testid="np-dob" /></Field>
            <Field label="Gender"><input className={inputCls} value={np.gender} onChange={(e) => setNp({ ...np, gender: e.target.value })} /></Field>
            <Field label="Preferred Name"><input className={inputCls} value={np.preferred_name} onChange={(e) => setNp({ ...np, preferred_name: e.target.value })} /></Field>
            <Field label="Aliases (comma-separated)"><input className={inputCls} value={np.aliases} onChange={(e) => setNp({ ...np, aliases: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setCreating(false)}>Cancel</button>
            <button className={btnPrimary} disabled={!np.first_name || !np.last_name} onClick={create} data-testid="np-save-btn">Create</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
